import { Icon } from "@iconify/react";
import { ReactNode, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ButtonPrimary, HStack, VStack } from "../../components/utils";
import {
  useCompanyManagers,
  useEmployeePageDetails,
} from "../../queries/employees";
import { useGetCustomFields } from "../../queries/customFields";
import { usePermissionStore } from "../../store/permissionStore";
import { determineUserAccessToResource } from "../../utils/auth";
import { Action, Resource } from "../../utils/interfaces/Companies";
import { UdfEntityType } from "../../types/CustomFields";
import { formatCurrency, getCurrencyType } from "../../utils/currencyFormatter";
import { formatDisplayDate } from "../../utils/date";

function hasDisplayValue(value: unknown) {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (value !== null && typeof value === "object")
    return Object.keys(value as Record<string, unknown>).length > 0;
  return true;
}

function displayValue(value: unknown) {
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function parseTransferAccessNote(note: string) {
  const match = note.match(
    /^access transferred to (.+?) from (.+?) on (.+?)(?:\.)?$/i
  );
  if (!match) return undefined;

  return {
    toEmail: match[1],
    fromEmail: match[2],
    transferredOn: match[3],
  };
}

function Detail({
  label,
  value,
  wide = false,
}: {
  label: string;
  value: unknown;
  wide?: boolean;
}) {
  if (!hasDisplayValue(value)) return null;

  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <p className="mb-1 text-xs font-normal text-secondaryText">{label}</p>
      <p className="whitespace-pre-wrap break-words text-sm font-medium text-primaryText">
        {displayValue(value)}
      </p>
    </div>
  );
}

function Card({
  title,
  children,
  className = "",
  action,
}: {
  title: string;
  children: ReactNode;
  className?: string;
  action?: ReactNode;
}) {
  return (
    <section
      className={`overflow-hidden rounded-lg border border-borderColor bg-white drop-shadow-box ${className}`}
    >
      <div className="flex items-center justify-between border-b border-borderColor px-5 py-4">
        <h2 className="text-sm font-medium text-gray-dark">{title}</h2>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

export default function Profile() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { permission } = usePermissionStore();
  const { data: employee, isLoading } = useEmployeePageDetails(id);
  const { data: companyManagers = [] } = useCompanyManagers();
  const { data: customFields = [] } = useGetCustomFields(
    UdfEntityType.EMPLOYEE
  );
  const canEdit = determineUserAccessToResource(
    permission?.aclList || [],
    Resource.Employee,
    Action.Edit
  );
  const managerNames = useMemo(() => {
    const byId = new Map(
      (Array.isArray(companyManagers) ? companyManagers : []).map((manager) => [
        String(manager.userId),
        manager.fullName || manager.emailId,
      ])
    );
    return (employee?.managers || [])
      .map((managerId) => byId.get(String(managerId)))
      .filter((managerName): managerName is string => Boolean(managerName));
  }, [companyManagers, employee?.managers]);

  const currency = getCurrencyType();

  if (isLoading || !employee) {
    return (
      <div className="h-72 rounded-lg border border-borderColor bg-white loading" />
    );
  }

  const tags = employee.additionalData?.tags || [];
  const comments = employee.additionalData?.comments || [];
  const transferAccessData = employee.additionalData?.transferAccessData || [];
  const udfValues = employee.additionalData?.udfValues || {};
  const populatedCustomFields = customFields.filter((field) =>
    hasDisplayValue(udfValues[field.fieldPath])
  );

  const dematAccounts = employee.dematAccounts || [];
  const nominees = employee.nominees || [];
  const salary =
    employee.salary === null || employee.salary === undefined
      ? null
      : formatCurrency(Number(employee.salary), currency);

  return (
    <div className="space-y-4 pb-8">
      <div className="grid items-start gap-4 lg:grid-cols-3">
        <div className="space-y-4">
          <Card title="Quick Info">
            <div className="space-y-4">
              <Detail label="Work email" value={employee.emailAddress} />
              <Detail label="Personal email" value={employee.personalEmail} />
              <Detail label="Contact number" value={employee.contactNumber} />
              <Detail label="PAN" value={employee.pan} />
              {transferAccessData.length > 0 && (
                <VStack className="gap-2">
                  <p className="text-xs font-normal text-secondaryText">
                    Access transfer history
                  </p>
                  <VStack className="gap-3">
                    {transferAccessData.map((transferNote, index) => {
                      const transfer = parseTransferAccessNote(transferNote);

                      return (
                        <VStack
                          key={`${transferNote}-${index}`}
                          className="gap-3 rounded-lg border border-borderColor bg-slate-light p-4"
                        >
                          <HStack className="w-full flex-wrap items-center justify-between gap-2">
                            <HStack className="items-center gap-2">
                              <Icon
                                aria-hidden="true"
                                icon="material-symbols:swap-horiz-rounded"
                                width={18}
                                className="shrink-0 text-secondary"
                              />
                              <span className="text-sm font-medium text-headerColor">
                                Access transferred
                              </span>
                            </HStack>
                            {transfer && (
                              <span className="text-xs text-secondaryText">
                                {transfer.transferredOn}
                              </span>
                            )}
                          </HStack>
                          {transfer ? (
                            <div className="grid gap-3 sm:grid-cols-2">
                              <VStack className="min-w-0 gap-1">
                                <span className="text-xs text-secondaryText">
                                  From email
                                </span>
                                <span className="break-all text-sm text-primaryText">
                                  {transfer.fromEmail}
                                </span>
                              </VStack>
                              <VStack className="min-w-0 gap-1">
                                <span className="text-xs text-secondaryText">
                                  To email
                                </span>
                                <span className="break-all text-sm text-primaryText">
                                  {transfer.toEmail}
                                </span>
                              </VStack>
                            </div>
                          ) : (
                            <p className="break-words text-sm leading-5 text-primaryText">
                              {transferNote}
                            </p>
                          )}
                        </VStack>
                      );
                    })}
                  </VStack>
                </VStack>
              )}
              {dematAccounts.length > 0 && (
                <VStack className="gap-2">
                  <p className="text-xs font-normal text-secondaryText">
                    Demat accounts
                  </p>
                  <VStack className="divide-y divide-borderColor border-y border-borderColor">
                    {dematAccounts.map((account, index) => (
                      <HStack
                        key={account.id || account.dematAccountId}
                        className="w-full flex-wrap items-start justify-between gap-2 py-3"
                      >
                        <VStack className="min-w-0 gap-1">
                          <p className="text-xs text-secondaryText">
                            Account {index + 1}
                          </p>
                          <p className="break-all text-sm font-medium text-primaryText">
                            {account.dematAccountId}
                          </p>
                        </VStack>
                        <HStack className="flex-wrap items-center gap-2">
                          <span className="rounded bg-tertiaryBg px-2 py-1 text-xs font-medium text-primaryText">
                            {account.type}
                          </span>
                          <span className="rounded bg-tertiaryBg px-2 py-1 text-xs font-medium capitalize text-primaryText">
                            {account.status}
                          </span>
                        </HStack>
                      </HStack>
                    ))}
                  </VStack>
                </VStack>
              )}
            </div>
          </Card>
          {tags.length > 0 && (
            <Card title="Tags">
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <span
                    key={tag.value}
                    className="rounded bg-tertiaryBg px-2.5 py-1 text-xs font-medium text-gray-dark"
                  >
                    {tag.label}
                  </span>
                ))}
              </div>
            </Card>
          )}
        </div>

        <div className="space-y-4 lg:col-span-2">
          <Card
            title="Professional Details"
            action={
              canEdit ? (
                <ButtonPrimary
                  onClick={() =>
                    navigate(`/options/employees/${id}/edit`, {
                      state: { returnTo: `/options/employeeDetails/${id}` },
                    })
                  }
                >
                  <span className="inline-flex items-center gap-2">
                    <Icon
                      aria-hidden="true"
                      icon="material-symbols:edit-outline"
                      width={16}
                    />
                    Edit Profile
                  </span>
                </ButtonPrimary>
              ) : null
            }
          >
            <div className="grid gap-x-10 gap-y-5 md:grid-cols-2">
              <Detail
                label="Employee ID"
                value={employee.employeeIdentification}
              />
              <Detail label="Legal name" value={employee.legalName} />
              <Detail label="Employee type" value={employee.employmentType} />
              <Detail
                label="Date of joining"
                value={formatDisplayDate(employee.dateOfJoin)}
              />
              <Detail
                label="Organization"
                value={employee.subsidiaryCompanyName || "Primary organization"}
              />
              <Detail label="Designation" value={employee.designation} />
              <Detail label="Department" value={employee.department} />
              <Detail
                label="Managers"
                value={managerNames.length ? managerNames : null}
              />
              <Detail label="Grade" value={employee.grade} />
              <Detail label="Salary" value={salary} />
              <Detail label="Notes" value={employee.notes} wide />
              {employee.resignationDate && (
                <Detail
                  label="Resignation date"
                  value={formatDisplayDate(employee.resignationDate)}
                />
              )}
              {employee.lastDay && (
                <Detail
                  label="Last working day"
                  value={formatDisplayDate(employee.lastDay)}
                />
              )}
            </div>
          </Card>

          <Card title="Personal Details">
            <div className="grid gap-x-10 gap-y-5 md:grid-cols-2">
              <Detail
                label="Country of residence"
                value={employee.residentStatus}
              />
              <Detail label="Nationality" value={employee.nationality} />
              <Detail label="Address" value={employee.address} wide />
            </div>
          </Card>

          {nominees.length > 0 && (
            <Card title="Nominees">
              <div className="space-y-3">
                {nominees.map((nominee, index) => (
                  <div
                    key={nominee.id || `${nominee.name}-${index}`}
                    className="rounded-lg border border-borderColor bg-slate-light p-4"
                  >
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2.5">
                        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-white text-primary shadow-box">
                          <Icon
                            aria-hidden="true"
                            icon="material-symbols:person-outline-rounded"
                            width={18}
                          />
                        </span>
                        <div>
                          <p className="text-sm font-medium text-gray-dark">
                            {nominee.name}
                          </p>
                          <p className="text-xs text-gray-light">
                            {nominee.relationship}
                          </p>
                        </div>
                      </div>
                      <span className="rounded bg-tertiaryBg px-2.5 py-1 text-xs font-medium text-primary">
                        {(Number(nominee.percentage) * 100)
                          .toFixed(2)
                          .replace(/\.00$/, "")}
                        % allocation
                      </span>
                    </div>
                    <div className="grid gap-x-10 gap-y-4 md:grid-cols-2">
                      <Detail
                        label="Date of birth"
                        value={
                          nominee.dateOfBirth
                            ? formatDisplayDate(nominee.dateOfBirth)
                            : null
                        }
                      />
                      <Detail label="Contact number" value={nominee.contact} />
                      <Detail label="Email" value={nominee.email} />
                      <Detail label="Address" value={nominee.address} />
                    </div>
                    {nominee.guardian && (
                      <div className="mt-4 border-t border-borderColor pt-4">
                        <p className="mb-3 text-xs font-medium uppercase tracking-wide text-secondaryText">
                          Guardian details
                        </p>
                        <div className="grid gap-x-10 gap-y-4 md:grid-cols-2">
                          <Detail
                            label="Guardian name"
                            value={nominee.guardian.name}
                          />
                          <Detail
                            label="Relationship"
                            value={nominee.guardian.relationship}
                          />
                          <Detail
                            label="Date of birth"
                            value={
                              nominee.guardian.dateOfBirth
                                ? formatDisplayDate(
                                    nominee.guardian.dateOfBirth
                                  )
                                : null
                            }
                          />
                          <Detail
                            label="Contact number"
                            value={nominee.guardian.contact}
                          />
                          <Detail
                            label="Email"
                            value={nominee.guardian.email}
                          />
                          <Detail
                            label="Address"
                            value={nominee.guardian.address}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {populatedCustomFields.length > 0 && (
            <Card title="Custom Fields">
              <div className="grid gap-x-10 gap-y-5 md:grid-cols-2">
                {populatedCustomFields.map((field) => (
                  <Detail
                    key={field.id}
                    label={field.name}
                    value={udfValues[field.fieldPath]}
                  />
                ))}
              </div>
            </Card>
          )}

          {comments.length > 0 && (
            <Card title="Comments">
              <div className="divide-y divide-borderColor">
                {comments.map((comment, index) => (
                  <p
                    key={index}
                    className="py-3 text-sm text-primaryText first:pt-0 last:pb-0"
                  >
                    {comment}
                  </p>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
